import { useWorkerBookingsData } from "@src/appV2/redesign/WorkerShifts/hooks/useWorkerBookingsData";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { isAfter, isBefore, parseISO } from "date-fns";

/**
 * Detects if a worker has an in-progress shift (not just upcoming).
 *
 * A shift is considered in-progress if:
 * - Shift start time is in the past AND shift end time is in the future
 *
 * This differs from highlightedBooking which also includes upcoming shifts within 12 hours.
 * For the mid-shift help intervention, we only want to show the modal when a shift is
 * actually in progress, not when it's upcoming.
 */
export function useHasUpcomingOrInProgressShift(): boolean {
  const { highlightedBooking, isLoading } = useWorkerBookingsData();

  // Don't return true while loading to avoid false positives
  if (isLoading) {
    return false;
  }

  // Check if there's a highlighted shift
  if (highlightedBooking?.type !== "shift") {
    return false;
  }

  // Additional check: ensure shift is actually in progress, not just upcoming
  const shift = highlightedBooking.data;
  const now = new Date();
  const shiftStart = parseISO(shift.start);
  const shiftEnd = parseISO(shift.end);

  return isBefore(shiftStart, now) && isAfter(shiftEnd, now);
}

/**
 * Returns the shift data for the in-progress shift.
 * Returns undefined if there's no such shift.
 * A shift is considered in-progress if its start time is in the past and end time is in the future.
 */
export function useUpcomingOrInProgressShiftData() {
  const { highlightedBooking, isLoading } = useWorkerBookingsData();
  const worker = useDefinedWorker();

  if (isLoading || highlightedBooking?.type !== "shift") {
    return undefined;
  }

  const shift = highlightedBooking.data;
  const workerTimezone = worker.tmz ?? new Intl.DateTimeFormat().resolvedOptions().timeZone;
  const workplaceTimezone = shift.facility.tmz ?? "";

  return {
    shift,
    facilityName: shift.facility.name,
    shiftStart: shift.start,
    shiftEnd: shift.end,
    workplaceTimezone,
    workerTimezone,
    workplaceId: shift.facility.userId ?? "",
  };
}
